import { ControllerRenderProps, FieldValues } from "react-hook-form";
import { specialQuery, SpecialQuery } from "src/formkit/utils";
import { FC, ReactNode, Suspense, useEffect, useMemo, useRef } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import { camelCase, get, isEqual, upperFirst } from "lodash";
import { useFormContext, useWatch } from "react-hook-form";

import { FieldError } from "src/components/field-error";
import { usePermissionContext } from "src/components/permission/permission-context";
import { Box } from "@hightouch/htui";

import {
  ComponentType,
  FormkitComponent,
  getUnaryBooleanValue,
  GraphQLVariables,
  HandlerGraphQLVariables,
  NodeType,
  resolveEntity,
  UnaryBoolean,
} from "@hightouch/formkit";
import { formkitRenderer } from "@hightouch/lib/templating/formkit-renderer";
import { collectStateReferenceKeys } from "src/formkit/component-utils";
import { useFormkitContext } from "src/formkit/components/formkit-context";

import { UseQueryOptions } from "react-query";
import { FKCollapsible } from "./collapsible";
import { FKAssociationMappings } from "./association-mappings";
import { FKButton } from "./button";
import { FKCheckbox } from "./checkbox";
import { FKSwitch } from "./switch";
import { FKSelect } from "./select";
import { FKInput } from "./input";
import { FKSecret } from "./secret";
import { FKKeyValueMapping } from "./key-value-mapping";
import { FKTable } from "./table";
import { FKTunnelTable } from "./tunnel-table";
import { FKTunnelSelect } from "./tunnel-select";
import { FKFile } from "./file";
import { FKTextArea } from "./text-area";
import { FKCode } from "./code";

import { FKRichTextEditor } from "./rich-text-editor";
import { FKEditor } from "./editor";
import { FKMessage } from "./message";
import { FKHeading } from "./heading";
import { FKLabel } from "./label";
import { FKMapping } from "./mapping";
import { FKMappings } from "./mappings";
import { FKRadioGroup } from "./radio-group";
import { FKColumn } from "./column";
import { FKColumnOrConstant } from "./column-or-constant";
import { FKNestedRadioGroup } from "./nested-radio-group";
import { FKNestedCheckboxGroup } from "./nested-checkbox-group";
import { FKCloudCredentialsSelect } from "./cloud-credentials-select";
import { FKGooglePicker } from "./google-picker";
import { FKJourneySettings } from "./journey-settings";
import { FKTemplate } from "./template";
import { FKTabs } from "./tabs";
import { useSearchParams } from "src/router";

const BaseComponent: Record<ComponentType, FC<any>> = {
  [ComponentType.Collapsible]: FKCollapsible,
  [ComponentType.Switch]: FKSwitch,
  [ComponentType.Checkbox]: FKCheckbox,
  [ComponentType.Select]: FKSelect,
  [ComponentType.Input]: FKInput,
  [ComponentType.Secret]: FKSecret,
  [ComponentType.KeyValueMapping]: FKKeyValueMapping,
  [ComponentType.Table]: FKTable,
  [ComponentType.TunnelTable]: FKTunnelTable,
  [ComponentType.TunnelSelect]: FKTunnelSelect,
  [ComponentType.File]: FKFile,
  [ComponentType.Textarea]: FKTextArea,
  [ComponentType.RichTextEditor]: FKRichTextEditor,
  [ComponentType.Editor]: FKEditor,
  [ComponentType.Code]: FKCode,
  [ComponentType.Message]: FKMessage,
  [ComponentType.Heading]: FKHeading,
  [ComponentType.Label]: FKLabel,
  [ComponentType.Mapping]: FKMapping,
  [ComponentType.Mappings]: FKMappings,
  [ComponentType.AssociationMappings]: FKAssociationMappings,
  [ComponentType.RadioGroup]: FKRadioGroup,
  [ComponentType.Button]: FKButton,
  [ComponentType.Column]: FKColumn,
  [ComponentType.ColumnOrConstant]: FKColumnOrConstant,
  [ComponentType.NestedRadioGroup]: FKNestedRadioGroup,
  [ComponentType.NestedCheckboxGroup]: FKNestedCheckboxGroup,
  [ComponentType.CloudCredentialsSelect]: FKCloudCredentialsSelect,
  [ComponentType.GooglePicker]: FKGooglePicker,
  [ComponentType.JourneySettings]: FKJourneySettings,
  [ComponentType.Template]: FKTemplate,
  [ComponentType.Tabs]: FKTabs,
};

const getComponent = (node: FormkitComponent) => BaseComponent[node.component];

export type ControllerField = ControllerRenderProps<FieldValues, string>;
export type SpecialOptions<TVariables extends GraphQLVariables> = {
  query: UseQueryOptions<unknown, unknown>;
  variables: TVariables;
};
export type QueryOptions = SpecialOptions<HandlerGraphQLVariables>;
export type ControllerRenderer = ({
  field,
}: {
  field: ControllerField;
}) => JSX.Element;

export interface SharedProps {
  name: string;
  error?: string;
  options?: QueryOptions;
  isSetup: boolean;
  isDisabled: boolean;
  query: SpecialQuery;
  fromQuery: SpecialQuery;
  template: Record<string, any>;
}

/**
 * This is the main component type for FormKit
 */
export const FKComponent: FC<{
  node: FormkitComponent;
  children?: ReactNode;
  formNodeContext?: any;
}> = ({ node, children, formNodeContext }) => {
  const baseContext = useFormkitContext();
  const featureFlags = useFlags();
  const {
    watch,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();
  const [searchParams] = useSearchParams();
  const focusKey = searchParams.get("focus");
  const focusRef = useRef<HTMLDivElement>(null);

  const focusMatch = useMemo(() => {
    if (!focusKey) return null;
    if (focusKey === node.key) return { type: "exact" as const };

    const escapedKey = node.key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const arrayMatch = focusKey.match(
      new RegExp(`^${escapedKey}\\[(\\d+)\\]$`),
    );
    // Matches key like `mappings[1]`
    if (arrayMatch?.[1]) {
      return { type: "array" as const, index: parseInt(arrayMatch[1], 10) };
    }

    return null;
  }, [focusKey, node.key]);

  // Watch fields referenced via StateReference so resolved props stay fresh.
  // An empty key list subscribes to nothing.
  const stateReferenceKeys = useMemo(
    () => collectStateReferenceKeys(node.props),
    [node.props],
  );
  const stateReferenceValues = useWatch({ name: stateReferenceKeys });

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const context = useMemo(
    () => ({ ...baseContext, formState: getValues(), featureFlags }),
    [baseContext, featureFlags, stateReferenceValues],
  );

  const props = useMemo(
    () =>
      resolveEntity({
        state: watch,
        context,
        entity: node.props,
        liquid: true,
        renderer: formkitRenderer,
      }),
    [context, node.props, watch],
  );

  const isFieldDisabled = baseContext.isFieldDisabled(node.key);

  const createError = (key: string) => {
    const rawError = get(errors, key)?.message;
    const error =
      typeof rawError === "string" ? rawError.replace(key, "This") : rawError;
    return error;
  };

  let error = createError(node.key);

  // Doing this because columns `{ from: string }` gets validate from inside out.
  // For example: a returned validation errors is `errors: { "eventId.from": "eventId.from is required."}`
  if (
    !error &&
    node.type === NodeType.Component &&
    [ComponentType.Column, ComponentType.ColumnOrConstant].includes(
      node.component,
    )
  ) {
    error = createError(`${node.key}.from`);
  }

  const value = useWatch({ name: node.key });

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (node.props?.default !== undefined && value === undefined) {
      setValue(node.key, node.props.default);
    }
  }, [node.key, node.props?.default, value]);

  const isChangedInDraft =
    !children &&
    baseContext?.draftChanges?.find(({ key }) => {
      const nodePath = node.key.split(".");
      const keyPath = key.split(".");
      return isEqual(nodePath, keyPath.slice(0, nodePath.length));
    })?.op;

  const permission = usePermissionContext();
  // the following components require the satisfaction of this permission as well
  // Switch, Checkbox, Secret, TextArea, File, RichTextEditor, Editor, Mapping, Mappings, AssociatedMappings, RadioGroup, Column, ColumnOrConstant
  //
  // the following currently only use the unarybooleanvalue of disable
  // KeyValueMapping, Table, TunnelTable, NestedRadioGroup, NestedCheckBoxGroup
  //
  // the following only care about this permission, which is effectively the same
  // Button
  //

  // I want to define as much shared state as possible before actually rendering the component
  const Component = getComponent(node);

  // Update the display name of the formkit component to make it easier to isolate in debugging
  Component.displayName = `Formkit${upperFirst(camelCase(node.component))}`;

  const queryOptions = () => {
    switch (node.component) {
      case ComponentType.Code: {
        return props.content;
      }
      case ComponentType.Input: {
        return props.value;
      }
      case ComponentType.Button: {
        return props.url;
      }
      default: {
        return props.options;
      }
    }
  };
  const isPropDisabled = getUnaryBooleanValue(
    props["disable"] as boolean | UnaryBoolean,
  );

  const sharedProps: SharedProps = {
    name: node.key,
    error: error?.toString(),
    isSetup: baseContext.isSetup,
    isDisabled: permission.unauthorized || isPropDisabled || isFieldDisabled,
    query: specialQuery(node.key, queryOptions() ?? []),
    fromQuery: specialQuery(node.key, props.fromOptions ?? []),
    template: specialQuery(node.key, props.template ?? {}),
  };
  const allProps = { ...props, ...sharedProps, formNodeContext };

  // Helper function to determine focus styles
  const getFocusStyles = () => {
    if (!focusMatch) return {};

    if (focusMatch.type === "exact") {
      return {
        input: { outline: "1px solid var(--chakra-colors-teal-border)" },
      };
    }

    // Array index match (e.g., mappings[1] when node.key is mappings)
    if (focusMatch.type === "array") {
      return {
        [`ul > li:nth-child(${focusMatch.index + 1})`]: {
          bg: "teal.background !important",
        },
      };
    }

    return {};
  };

  useEffect(() => {
    if (focusMatch) {
      focusRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [focusMatch]);

  return (
    <Box
      ref={focusRef}
      sx={{
        ...(isChangedInDraft
          ? {
              position: "relative",
              "::after": {
                content: '""',
                top: 0,
                left: -5,
                display: "block",
                width: "4px",
                height: "100%",
                position: "absolute",
                borderRadius: "2px",
                backgroundColor:
                  isChangedInDraft === "add" ? "success.base" : "danger.base",
              },
            }
          : {}),
        ...getFocusStyles(),
      }}
    >
      <Suspense fallback={null}>
        <Component {...allProps}> {children} </Component>
      </Suspense>

      <FieldError
        error={
          (allProps.query?.error as string) ||
          (allProps.fromQuery?.error as string) ||
          allProps.error
        }
      />
    </Box>
  );
};

/**
 * Unlike FKComponent, FormNode can also be a layout or modifier
 */
export { FKNode } from "./formkit-node";

// TODO: Document this and what makes it unique; rename references to it
export { FKProcessNode as ProcessFormNode } from "./formkit-process-node";
